import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createUri, Api } from '@api/utils';
import { Query } from './utils';

export interface AuditDelta {
    before: unknown;
    after: unknown;
}

export interface AuditItem {
    entityPk: string;
    userId: string;
    ts: string;
    email: string;
    op: string;
    delta: Record<string, AuditDelta>;
}

// New interfaces for the search functionality
export interface AuditLogRecord {
    entityPk: string;
    ts: string;
    op: string;
    email: string;
    userId: string;
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    delta?: Record<string, unknown>;
    expiresEpoch?: number;
}

export interface PaginationInfo {
    lastEvaluatedKey?: Record<string, unknown>;
    hasMore: boolean;
    totalScanned: number;
}

export interface AuditSearchResponse {
    items: AuditLogRecord[];
    pagination: PaginationInfo;
}

export interface AuditSearchParams {
    token: string;
    operationType?: string;
    entityType?: string;
    entityPk?: string;
    fieldsChanged?: string;
    from?: string;
    to?: string;
    limit?: number;
    lastEvaluatedKey?: string;
}

export const auditHooks = createApi({
    reducerPath: 'auditApi',
    baseQuery: fetchBaseQuery({
        baseUrl: createUri(Api.Audit, '/audit/V1'),
    }),
    endpoints: (build) => ({
        auditHistory: build.query<
            AuditItem[],
            { token: string; entityPk?: string; userId?: string; from?: string; to?: string }
        >({
            query: ({ token, ...params }) => ({
                url: '/history',
                method: 'GET',
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
        recentChanges: build.query<
            AuditItem[],
            { token: string; entityPk?: string; userId?: string; from?: string; to?: string }
        >({
            query: ({ token, ...params }) => ({
                url: '/recent',
                method: 'GET',
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
        auditSearch: build.query<AuditSearchResponse, AuditSearchParams>({
            query: ({ token, ...params }) => ({
                url: '/search',
                method: 'GET',
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
    }),
});

// Wrapper that automatically injects the Cognito IdToken
const audit = {
    useAuditHistoryQuery: Query.withToken(auditHooks.useAuditHistoryQuery),
    useRecentChangesQuery: Query.withToken(auditHooks.useRecentChangesQuery),
    useAuditSearchQuery: Query.withToken(auditHooks.useAuditSearchQuery),
};

export const { useAuditHistoryQuery, useRecentChangesQuery, useAuditSearchQuery } = audit;
